import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  Select,
  Text,
  VStack,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';
import { FaEdit } from 'react-icons/fa';
import { AddressType } from '@Encore-FastStartup-Phase-Out/encore-shared';

import { useStore } from '../../hooks/useStore';
import DefaultProfile from '../../assets/png/default-profile.png';
import ConfirmationModal from '../modals/ConfirmationModal';
import { Colors } from '../../theme/colors';
import EditCreditModal from '../modals/EditCreditModal';
import EditDebtModal from '../modals/EditDebtModal';

import InfoDisplay from './InfoDisplay';
import EnableDisableSeller from './user/EnableDisableSeller';
import ReviewUserRequest from './user/ReviewUserRequest';
import FlashFormatSetting from './FlashFormatSetting';
import PayoutScheduleSetting from './PayoutScheduleSetting';
import ManuallyVerifyPhoneNumber from './user/ManuallyVefifyPhoneNumber';
import BypassPaymentFailures from './user/BypassPaymentFailures';

type UserDetailsProps = {
  onDelete: () => Promise<void>;
};

enum LockAction {
  lock = 'Lock',
  unlock = 'Unlock',
}

const UserDetails = ({ onDelete }: UserDetailsProps) => {
  const {
    userStore: {
      displayedUser,
      checkUsername,
      checkEmail,
      updateUser,
      makeUserEligibleForReferralTest,
      deleteAllUserMessages,
      deleteAllUserBids,
      fetchUsers,
    },
    listingStore: { deleteAllActiveUserListings },
  } = useStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteAllMessagesModalOpen, setIsDeleteAllMessagesModalOpen] =
    useState(false);
  const [isDeleteAllBidsModalOpen, setIsDeleteAllBidsModalOpen] =
    useState(false);
  const [isDeleteAllListingsModalOpen, setIsDeleteAllListingsModalOpen] =
    useState(false);
  const [isLockUserModalOpen, setIsLockUserModalOpen] = useState(false);
  const [isEditCreditBalanceOpen, setIsEditCreditBalanceOpen] = useState(false);
  const [isEditDebtBalanceOpen, setIsEditDebtBalanceOpen] = useState(false);
  const [isEditingSellerFee, setIsEditingSellerFee] = useState(false);
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [sellerFee, setSellerFee] = useState(displayedUser.sellerFee);
  const [username, setUsername] = useState(displayedUser.userName || '');
  const [email, setEmail] = useState(displayedUser.email || '');
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const [isEditingSalesTax, setIsEditingSalesTax] = useState(false);
  const [exemptFromSalesTaxState, setExemptFromSalesTaxState] = useState(
    displayedUser.exemptFromSalesTax,
  );
  const textColor = useColorModeValue(
    Colors.light.fadedText,
    Colors.dark.fadedText,
  );

  const lockOrUnlockAction = displayedUser.lockedAt
    ? LockAction.unlock
    : LockAction.lock;

  const userInfo = [
    { title: 'Amount Spent:', value: displayedUser.amountSpent || 'No data' },
    { title: 'Amount Sold:', value: displayedUser.amountSold || 'No data' },
    {
      title: 'Credit Value:',
      value: displayedUser.credits,
    },
    {
      title: 'Debt Value:',
      value: displayedUser.debt || 0,
    },
    { title: 'Signed Up Date:', value: displayedUser.createdAt || 'No data' },
    {
      title: 'Seller stripe account id:',
      value: displayedUser.stripeUserId || 'No data',
    },
    {
      title: 'Seller customer account id:',
      value: displayedUser.stripeCustomerId || 'No data',
    },
    {
      title: 'Payment Failed Strikes:',
      value: displayedUser.paymentFailedStrikes || 'No data',
    },
    {
      title: 'Bypass Payment Failures:',
      value: displayedUser.bypassPaymentFailures ? 'Yes' : 'No',
    },
    {
      title: 'Eligible for Credit:',
      value: displayedUser.eligibleForAppCredits ? 'Yes' : 'No',
    },
  ];

  const handleDelete = async () => {
    await onDelete();
    setIsModalOpen(false);
  };

  const handleDeleteAllMessages = async () => {
    try {
      await deleteAllUserMessages();
      toast({
        title: 'Success',
        description: 'User messages deleted successfully!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await fetchUsers({ page: 1, limit: 10 });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete user messages!',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDeleteAllMessagesModalOpen(false);
    }
  };

  const handleDeleteAllListings = async () => {
    try {
      await deleteAllActiveUserListings(displayedUser.id);
      toast({
        title: 'Success',
        description: 'User listings deleted successfully!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await fetchUsers({ page: 1, limit: 10 });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete user listings!',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDeleteAllListingsModalOpen(false);
    }
  };

  const handleDeleteAllBids = async () => {
    try {
      await deleteAllUserBids();
      toast({
        title: 'Success',
        description: 'User bids deleted successfully!',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      await fetchUsers({ page: 1, limit: 10 });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete user bids!',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDeleteAllBidsModalOpen(false);
    }
  };

  const handleClick = () => {
    setIsModalOpen(true);
  };

  const handleClickDeleteAllListings = () => {
    setIsDeleteAllListingsModalOpen(true);
  };

  const handleClickDeleteAllMessages = () => {
    setIsDeleteAllMessagesModalOpen(true);
  };

  const handleClickDeleteAllBids = () => {
    setIsDeleteAllBidsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleDeleteAllMessagesModalClose = () => {
    setIsDeleteAllMessagesModalOpen(false);
  };

  const handleDeleteAllBidsModalClose = () => {
    setIsDeleteAllBidsModalOpen(false);
  };

  const handleLockUnlockClick = () => {
    setIsLockUserModalOpen(true);
  };

  const handleLockUserModalClose = () => {
    setIsLockUserModalOpen(false);
  };

  const handleClickEditCredit = () => {
    setIsEditCreditBalanceOpen(true);
  };

  const handleCloseDeleteAllListingsModal = () => {
    setIsDeleteAllListingsModalOpen(false);
  };

  const handleCloseEditCreditModal = () => {
    setIsEditCreditBalanceOpen(false);
  };

  const handleClickEditDebt = () => {
    setIsEditDebtBalanceOpen(true);
  };

  const handleCloseEditDebtModal = () => {
    setIsEditDebtBalanceOpen(false);
  };

  const handleLockUnlockUser = async () => {
    await updateUser(displayedUser.id, {
      lockedAt: displayedUser.lockedAt ? null : new Date(),
    });

    setIsLockUserModalOpen(false);
  };

  const handleClickCreditReferralTest = async () => {
    await makeUserEligibleForReferralTest();
  };

  const handleEditSellerFee = () => {
    setIsEditingSellerFee(true);
  };

  const handleSaveSellerFee = async () => {
    try {
      setLoading(true);
      await updateUser(displayedUser.id, {
        sellerFee,
      });
      setIsEditingSellerFee(false);
      toast({
        title: 'Success',
        description: 'Seller fee updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      console.error(e);
      toast({
        title: 'Error',
        description: 'Failed to update seller fee',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSellerFeeChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setSellerFee(parseInt(event.target.value));
  };

  const handleEditUsername = () => {
    setIsEditingUsername(true);
  };

  const handleEditEmail = () => {
    setIsEditingEmail(true);
  };

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(event.target.value);
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handleSaveUsername = async () => {
    try {
      setLoading(true);
      const checkIfUsernameExists = await checkUsername(username);

      if (checkIfUsernameExists.exists) {
        toast({
          title: 'Error',
          description: 'Username already exists',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      await updateUser(displayedUser.id, {
        userName: username,
      });
      setIsEditingUsername(false);
      toast({
        title: 'Success',
        description: 'Username updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      console.error(e);
      toast({
        title: 'Error',
        description: 'Failed to update username',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEmail = async () => {
    try {
      setLoading(true);
      const checkIfEmailExists = await checkEmail(email);

      if (checkIfEmailExists.exists) {
        toast({
          title: 'Error',
          description: 'Email already exists',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      await updateUser(displayedUser.id, {
        email,
      });
      setIsEditingEmail(false);
      toast({
        title: 'Success',
        description: 'Email updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      console.error(e);
      toast({
        title: 'Error',
        description: 'Failed to update email',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handlers for editing Sales Tax
  const handleEditSalesTax = () => {
    setIsEditingSalesTax(true);
  };

  const handleSalesTaxChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setExemptFromSalesTaxState(event.target.value === 'true');
  };

  const handleSaveSalesTax = async () => {
    try {
      setLoading(true);
      await updateUser(displayedUser.id, {
        exemptFromSalesTax: exemptFromSalesTaxState,
      });
      setIsEditingSalesTax(false);
      toast({
        title: 'Success',
        description: 'Sales tax updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      console.error(e);
      toast({
        title: 'Error',
        description: 'Failed to update sales tax',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const shippingInfo = useMemo(() => {
    if (
      !displayedUser.shippingInfo ||
      displayedUser.shippingInfo.length === 0
    ) {
      return null;
    }

    // for older users that might accidentally have multiple addresses of the same type
    const latestDefaultAddress = [...displayedUser.shippingInfo]
      .reverse()
      .find((info) => info.type === AddressType.DEFAULT);

    const latestReturnAddress = [...displayedUser.shippingInfo]
      .reverse()
      .find((info) => info.type === AddressType.RETURN_ADDRESS);

    return { latestDefaultAddress, latestReturnAddress };
  }, [displayedUser.shippingInfo]);

  const renderShippingInfo = (info: (typeof displayedUser.shippingInfo)[0]) => (
    <VStack align="start" spacing={1} key={info.type}>
      <Text fontWeight="bold">
        {info.type === AddressType.DEFAULT
          ? 'Default Address'
          : 'Return Address'}
      </Text>
      <Text>{`${info.firstName} ${info.lastName}`}</Text>
      <Text>{info.addressLineOne}</Text>
      {info.addressLineTwo && <Text>{info.addressLineTwo}</Text>}
      <Text>{`${info.city}, ${info.state} ${info.zipCode}`}</Text>
      <Text>{info.country}</Text>
      <Text>{`${displayedUser.countryCode} ${displayedUser.phoneNumber}`}</Text>
    </VStack>
  );

  return (
    <VStack align="center" spacing={4}>
      <Avatar size="xl" src={displayedUser.avatar || DefaultProfile} />
      <HStack>
        {isEditingUsername ? (
          <>
            <Input value={username} onChange={handleUsernameChange} />
            <Button onClick={handleSaveUsername} isLoading={loading}>
              Save
            </Button>
          </>
        ) : (
          <Flex align="center" gap={4} mb={4}>
            <Heading>{displayedUser.userName || 'No username'}</Heading>
            <IconButton
              aria-label="Edit username"
              icon={<FaEdit />}
              onClick={handleEditUsername}
            />
          </Flex>
        )}
      </HStack>
      <Box
        display="flex"
        flex={1}
        w="100%"
        justifyContent="space-between"
        alignItems="center"
      >
        <Text flex={1}>Email:</Text>
        {isEditingEmail ? (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Input flex={1} value={email} onChange={handleEmailChange} />
            <Button onClick={handleSaveEmail} isLoading={loading} ml={2}>
              Save
            </Button>
          </Box>
        ) : (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flex={1}
            w="100%"
          >
            <Text flex={1} color={textColor}>
              {displayedUser.email || 'No email'}
            </Text>
            <IconButton
              aria-label="Edit email"
              icon={<FaEdit />}
              onClick={handleEditEmail}
              ml={2}
            />
          </Box>
        )}
      </Box>

      {userInfo.map((info, index) => (
        <InfoDisplay key={index} title={info.title} value={info.value} />
      ))}
      <HStack w="100%" justify="center" p={2} gap={10}>
        <Text fontWeight="bold">Sales Tax:</Text>
        {isEditingSalesTax ? (
          <>
            <Select
              value={exemptFromSalesTaxState ? 'true' : 'false'}
              onChange={handleSalesTaxChange}
            >
              <option value="false">Sales tax on</option>
              <option value="true">Sales tax exempt</option>
            </Select>
            <Button onClick={handleSaveSalesTax} isLoading={loading}>
              Save
            </Button>
          </>
        ) : (
          <HStack>
            <Text pr={10}>
              {exemptFromSalesTaxState ? 'Sales tax exempt' : 'Sales tax on'}
            </Text>
            <IconButton
              aria-label="Edit sales tax"
              icon={<FaEdit />}
              onClick={handleEditSalesTax}
            />
          </HStack>
        )}
      </HStack>
      {displayedUser.stripeUserId && (
        <HStack w="100%" justify="center" p={2} gap={10}>
          <Text fontWeight="bold">Seller Fee:</Text>
          {isEditingSellerFee ? (
            <>
              <Select value={sellerFee} onChange={handleSellerFeeChange}>
                {Array.from({ length: 26 }, (_, i) => (
                  <option key={i} value={i}>
                    {i}%
                  </option>
                ))}
              </Select>
              <Button onClick={handleSaveSellerFee} isLoading={loading}>
                Save
              </Button>
            </>
          ) : (
            <HStack>
              <Text pr={10}>{sellerFee}%</Text>
              <IconButton
                aria-label="Edit seller fee"
                icon={<FaEdit />}
                onClick={handleEditSellerFee}
              />
            </HStack>
          )}
        </HStack>
      )}
      {displayedUser.stripeUserId && <FlashFormatSetting />}
      {displayedUser.stripeUserId && <PayoutScheduleSetting />}
      {shippingInfo && (
        <VStack
          w="100%"
          align="start"
          spacing={4}
          p={4}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
        >
          <Heading size="md">Shipping Information</Heading>
          {shippingInfo.latestDefaultAddress && (
            <Box>
              {renderShippingInfo(shippingInfo.latestDefaultAddress)}
              <Divider my={2} />
            </Box>
          )}
          {shippingInfo.latestReturnAddress &&
            renderShippingInfo(shippingInfo.latestReturnAddress)}
        </VStack>
      )}
      {displayedUser.pendingRequest && <ReviewUserRequest />}
      <EnableDisableSeller />
      <ManuallyVerifyPhoneNumber />
      <BypassPaymentFailures />
      <Button
        w={'100%'}
        py="25px"
        color="white"
        bg="blue.400"
        variant="contained"
        border="2px"
        borderColor="blue.400"
        onClick={handleClickCreditReferralTest}
      >
        Enable Credit
      </Button>
      <Button
        w={'100%'}
        py="25px"
        color="green.400"
        variant="contained"
        border="2px"
        borderColor="green.400"
        onClick={handleClickEditCredit}
      >
        Edit Credit
      </Button>
      <Button
        w={'100%'}
        py="25px"
        color="red.400"
        variant="contained"
        border="2px"
        borderColor="red.400"
        onClick={handleClickEditDebt}
      >
        Edit Debt
      </Button>
      <Button
        onClick={handleClickDeleteAllListings}
        w="100%"
        py="25px"
        bg="red.700"
      >
        Delete all listings
      </Button>
      <Button
        onClick={handleClickDeleteAllMessages}
        w="100%"
        py="25px"
        bg="red.500"
      >
        Delete all messages
      </Button>
      <Button
        onClick={handleClickDeleteAllBids}
        w="100%"
        py="25px"
        bg="red.600"
      >
        Delete all bids
      </Button>
      <Button
        onClick={handleLockUnlockClick}
        w="100%"
        py="25px"
        color={lockOrUnlockAction === LockAction.lock ? 'red.400' : 'green.400'}
      >
        {lockOrUnlockAction} user
      </Button>
      <Button onClick={handleClick} w="100%" bg="red.400" py="25px">
        Delete user
      </Button>
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title={'Delete user?'}
        subtitle={'This action cannot be undone'}
        cancelActionLabel={'Cancel'}
        confirmActionLabel={'Delete'}
        onCancel={handleModalClose}
        onConfirm={handleDelete}
      />
      <ConfirmationModal
        isOpen={isDeleteAllMessagesModalOpen}
        onClose={handleDeleteAllMessagesModalClose}
        title={'Delete all user auction messages, chats, private messages?'}
        subtitle={'This action cannot be undone'}
        cancelActionLabel={'Cancel'}
        confirmActionLabel={'Delete'}
        onCancel={handleDeleteAllMessagesModalClose}
        onConfirm={handleDeleteAllMessages}
      />
      <ConfirmationModal
        isOpen={isDeleteAllListingsModalOpen}
        onClose={handleCloseDeleteAllListingsModal}
        title={'Delete all user listings?'}
        subtitle={'This action cannot be undone'}
        cancelActionLabel={'Cancel'}
        confirmActionLabel={'Delete'}
        onCancel={handleCloseDeleteAllListingsModal}
        onConfirm={handleDeleteAllListings}
      />
      <ConfirmationModal
        isOpen={isDeleteAllBidsModalOpen}
        onClose={handleDeleteAllBidsModalClose}
        title={'Delete all user auction bids, pre-bids, max-bids?'}
        subtitle={'This action cannot be undone'}
        cancelActionLabel={'Cancel'}
        confirmActionLabel={'Delete'}
        onCancel={handleDeleteAllBidsModalClose}
        onConfirm={handleDeleteAllBids}
      />
      <ConfirmationModal
        isOpen={isLockUserModalOpen}
        onClose={handleLockUserModalClose}
        title={`${lockOrUnlockAction} user?`}
        subtitle={'This action can be undone.'}
        cancelActionLabel={'Cancel'}
        confirmActionLabel={lockOrUnlockAction}
        onCancel={handleLockUserModalClose}
        onConfirm={handleLockUnlockUser}
      />
      <EditCreditModal
        isOpen={isEditCreditBalanceOpen}
        onClose={handleCloseEditCreditModal}
      />
      <EditDebtModal
        isOpen={isEditDebtBalanceOpen}
        onClose={handleCloseEditDebtModal}
      />
    </VStack>
  );
};

export default observer(UserDetails);
